/**
 * Role Permission Synchronization Utilities
 * Helps sync simple roles with complex permission groups
 */

import { ROLES, ROLE_TO_GROUPS, hasPermission } from "./rolePermissions";

/**
 * Check if a staff member's permission groups match their role
 * @param {Object} staffMember - Staff member object
 * @returns {Object} Analysis of permission alignment
 */
export const analyzeRolePermissionAlignment = (staffMember) => {
  if (!staffMember) {
    return { aligned: false, reason: "No staff member provided" };
  }

  const { role, permissions } = staffMember;
  const expectedGroups = ROLE_TO_GROUPS[role] || [];
  const actualGroups = permissions?.groups || [];

  const missingGroups = expectedGroups.filter((group) => !actualGroups.includes(group));
  const extraGroups = actualGroups.filter((group) => !expectedGroups.includes(group));

  const aligned = missingGroups.length === 0 && extraGroups.length === 0;

  return {
    aligned,
    role,
    expectedGroups,
    actualGroups,
    missingGroups,
    extraGroups,
    hasPermissionSystem: !!permissions,
    needsUpdate: missingGroups.length > 0,
  };
};

/**
 * Get the permissions object that should be set for a role
 * @param {string} role - User's role
 * @param {Object} existingPermissions - Existing permissions to preserve
 * @returns {Object} Permissions structure
 */
export const getPermissionsForRole = (role, existingPermissions = {}) => {
  const expectedGroups = ROLE_TO_GROUPS[role] || [];

  return {
    groups: expectedGroups,
    inheritRole: existingPermissions.inheritRole !== undefined ? existingPermissions.inheritRole : true,
    modules: existingPermissions.modules || {},
    denials: existingPermissions.denials || [],
  };
};

/**
 * Check if a staff member has the minimum required permissions for their role
 * @param {Object} staffMember - Staff member object
 * @returns {boolean}
 */
export const hasMinimumPermissionsForRole = (staffMember) => {
  const analysis = analyzeRolePermissionAlignment(staffMember);

  // If they have extra groups, that's fine - they have at least the minimum
  return analysis.missingGroups.length === 0;
};

/**
 * Get a user-friendly description of permission issues
 * @param {Object} staffMember - Staff member object
 * @returns {string}
 */
export const getPermissionIssueDescription = (staffMember) => {
  const analysis = analyzeRolePermissionAlignment(staffMember);

  if (analysis.aligned) {
    return "Permissions are correctly aligned with role";
  }

  if (!analysis.hasPermissionSystem) {
    return "User has no advanced permission system configured";
  }

  const issues = [];

  if (analysis.missingGroups.length > 0) {
    issues.push(`Missing groups: ${analysis.missingGroups.join(", ")}`);
  }

  if (analysis.extraGroups.length > 0) {
    issues.push(`Extra groups: ${analysis.extraGroups.join(", ")}`);
  }

  return issues.join("; ");
};

/**
 * Determine effective permissions for a staff member
 * Combines role-based and group-based permissions
 * @param {Object} staffMember - Staff member object
 * @returns {Object} Effective permissions summary
 */
export const getEffectivePermissions = (staffMember) => {
  if (!staffMember) return { canView: {}, canEdit: {}, canDelete: {} };

  const { role, permissions } = staffMember;
  const hasSystemAdmin = permissions?.groups?.includes("system-admin");
  const hasHighLevelAccess = permissions?.groups?.some((group) =>
    ["system-admin", "department-manager", "team-lead"].includes(group)
  );

  // If system admin, can do everything
  if (hasSystemAdmin) {
    return {
      canView: {
        dashboard: true,
        bookings: true,
        schedules: true,
        drivers: true,
        accounts: true,
        organization: true,
        calendars: true,
        configManagement: true,
        regions: true,
        dataMigration: true,
        ghostDocuments: true,
        exportAreaSettings: true,
      },
      canEdit: {
        dashboard: true,
        bookings: true,
        schedules: true,
        drivers: true,
        accounts: true,
        organization: true,
        calendars: true,
        configManagement: true,
        regions: true,
        dataMigration: true,
        ghostDocuments: true,
        exportAreaSettings: true,
      },
      canDelete: {
        dashboard: true,
        bookings: true,
        schedules: true,
        drivers: true,
        accounts: true,
        organization: true,
        calendars: true,
        configManagement: true,
        regions: true,
        dataMigration: true,
        ghostDocuments: true,
        exportAreaSettings: true,
      },
      isSystemAdmin: true,
      hasAdvancedPermissions: true,
    };
  }

  // Build permissions based on role and groups
  const pages = [
    "dashboard",
    "bookings",
    "schedules",
    "drivers",
    "accounts",
    "organization",
    "calendars",
    "configManagement",
    "regions",
    "exportAreaSettings",
  ];
  const canView = {};
  const canEdit = {};
  const canDelete = {};

  // Apply role permissions logic using the centralized permission system
  pages.forEach((page) => {
    canView[page] = hasPermission(role, page, "view");
    canEdit[page] = hasPermission(role, page, "edit");
    canDelete[page] = hasPermission(role, page, "delete");
  });

  return {
    canView,
    canEdit,
    canDelete,
    isSystemAdmin: false,
    hasAdvancedPermissions: permissions?.groups?.length > 0,
    hasHighLevelAccess,
  };
};

export default {
  analyzeRolePermissionAlignment,
  getPermissionsForRole,
  hasMinimumPermissionsForRole,
  getPermissionIssueDescription,
  getEffectivePermissions,
};
